import {logFathomEvent, setLoginAnalytics} from 'notability-services-common-frontend'
import * as Sentry from '@sentry/react'
import {OAuthProvider, Platform, getOAuthState, localesToLanguageCode} from 'notability-services-common-universal'
import {useCallback, useEffect, useRef, useState} from 'react'

import {SsoButtonTemplate} from './SsoButtonTemplate'
import {config} from '../config'
import {SSOButtonProps} from './GoogleLoginButton'
import {useCustomRouter} from '../util/useCustomRouter'

const loadScript = (src: string): Promise<HTMLScriptElement> => {
  return new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${src}"]`)
    if (existingScript) {
      resolve(existingScript as HTMLScriptElement)
    } else {
      const script = document.createElement('script')
      script.src = src
      script.async = true
      script.onload = () => resolve(script)
      script.onerror = () => reject(new Error('Failed to load Apple ID SDK'))
      document.body.appendChild(script)
    }
  })
}

const fetchNonce = async (): Promise<string | undefined> => {
  try {
    const nonceResult = await fetch(`${config.API_SERVICE_URL}/auth/nonce`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'}
    })

    const body = await nonceResult.json()
    return body.nonce
  } catch (e) {
    console.error(e)
  }
}

export function AppleLoginButton({setShowOAuthError, isFromWebApp}: SSOButtonProps) {
  const [nonce, setNonce] = useState<string | undefined>(undefined)
  const signInFetched = useRef(false)
  const router = useCustomRouter()
  const [scriptHasLoaded, setScriptHasLoaded] = useState(false)

  const handleLoginSuccess = useCallback(
    async (auth: AppleSignInAPI.AuthorizationI) => {
      if (signInFetched.current) return
      signInFetched.current = true
      setShowOAuthError(false)

      try {
        const state = getOAuthState(
          Platform.WEB,
          OAuthProvider.APPLE,
          localesToLanguageCode(navigator.languages),
          isFromWebApp
        )

        const glBackendResponse = await fetch(`${config.API_SERVICE_URL}/apple/sign-in`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            code: auth.code,
            id_token: auth.id_token,
            state
          })
        })

        setLoginAnalytics({
          type: OAuthProvider.APPLE
        })

        if (glBackendResponse.status === 200) {
          logFathomEvent('SSO Complete - Apple')
          setNonce(undefined)
          router.pushLoginComplete()
        } else if (glBackendResponse.status === 401) {
          setShowOAuthError(true)
        } else if (glBackendResponse.status === 403) {
          router.pushEmailLinkConfirmation('sign-in')
        }
      } catch (error) {
        Sentry.captureException(error)
        setShowOAuthError(true)
      } finally {
        signInFetched.current = false
        setNonce(undefined)
      }
    },
    [setShowOAuthError, isFromWebApp, router]
  )

  useEffect(() => {
    if (scriptHasLoaded && !nonce) {
      fetchNonce().then((nonceReturn) => {
        if (nonceReturn !== undefined) {
          setNonce(nonceReturn)
        } else {
          throw new Error('Undefined nonce received from API')
        }
      })
    }
  }, [scriptHasLoaded, nonce])

  useEffect(() => {
    if (!scriptHasLoaded) {
      loadScript('https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js').then(() =>
        setScriptHasLoaded(true)
      )
    }
  }, [scriptHasLoaded, setScriptHasLoaded])

  useEffect(() => {
    const handleAppleIDSignInSuccess = (event: CustomEvent<AppleSignInAPI.SignInResponseI>) => {
      handleLoginSuccess(event.detail.authorization)
    }
    const handleAppleIDSignInFailure = (event: CustomEvent<AppleSignInAPI.SignInErrorI>) => {
      if (event.detail.error === 'popup_closed_by_user') return
      setShowOAuthError(true)
    }
    document.addEventListener('AppleIDSignInOnSuccess', handleAppleIDSignInSuccess as EventListener)
    document.addEventListener('AppleIDSignInOnFailure', handleAppleIDSignInFailure as EventListener)
    return () => {
      document.removeEventListener('AppleIDSignInOnSuccess', handleAppleIDSignInSuccess as EventListener)
      document.removeEventListener('AppleIDSignInOnFailure', handleAppleIDSignInFailure as EventListener)
    }
  }, [setShowOAuthError, handleLoginSuccess])

  useEffect(() => {
    const state = JSON.stringify(
      getOAuthState(Platform.WEB, OAuthProvider.APPLE, localesToLanguageCode(navigator.languages), isFromWebApp)
    )

    if (nonce) {
      AppleID.auth.init({
        clientId: config.APPLE_CLIENT_ID,
        scope: config.APPLE_SCOPES,
        redirectURI: config.APPLE_REDIRECT_URI,
        state,
        nonce,
        usePopup: true
      })
    }
  }, [nonce, isFromWebApp])

  const appleSignin = useCallback(() => {
    logFathomEvent('SSO Open - Apple')
    AppleID.auth.signIn({scope: 'name email'}).catch((error) => {
      if (error.error !== 'popup_closed_by_user') {
        Sentry.captureException(error)
        setShowOAuthError(true)
      }
    })
  }, [setShowOAuthError])

  // See https://developer.apple.com/documentation/sign_in_with_apple/clientconfigi/3230956-state
  // for documentation on Sign in with Apple
  return <SsoButtonTemplate provider={'Apple'} onClick={appleSignin} />
}
